import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Camera, Users, Heart, Zap, ScanFace, Presentation, MonitorPlay,
  ChevronDown, Copy, ExternalLink, Settings, ImageIcon, Sparkles, FileArchive,
} from 'lucide-react';
import { AlbumWithEvent } from '../../types';
import { Modal } from '../UI/Modal';
import { LiveBadge } from '../UI/LiveBadge';
import { InfoTip } from '../UI/InfoTip';
import styles from './CreatorControlsModal.module.css';

interface ActivityStats {
  totalPhotos: number;
  totalMembers: number;
  totalLikes: number;
  pendingProcessing?: number;
}

interface FaceStats {
  totalPhotos: number;
  photosIndexed: number;
  photosWithFaces: number;
  photosWithoutFaces: number;
  photosNotYetIndexed: number;
  facesIndexedCount: number;
  collectionId: string | null;
  membersWithFaceRecognition: number;
  membersWithoutFaceRecognition: number;
  totalMembers: number;
}

interface CreatorControlsModalProps {
  isOpen: boolean;
  onClose: () => void;
  album: AlbumWithEvent | null;
  isLive: boolean;
  faceEnabled: boolean;
  eventId: string;
  totalPhotoCount: number;
  photoCount: number;
  activityStats: ActivityStats | null;
  onPreviewSlideshow: () => void;
  onSlideshowSetup: () => void;
}

function liveSinceLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function CreatorControlsModal({
  isOpen,
  onClose,
  album,
  isLive,
  faceEnabled,
  eventId,
  totalPhotoCount,
  photoCount,
  activityStats,
  onPreviewSlideshow,
  onSlideshowSetup,
}: CreatorControlsModalProps) {
  const [faceStats, setFaceStats] = useState<FaceStats | null>(null);
  const [faceStatsLoading, setFaceStatsLoading] = useState(false);
  const [venueMenuOpen, setVenueMenuOpen] = useState(false);
  const venueMenuRef = useRef<HTMLDivElement>(null);

  // Fetch face stats only when modal is open and face recognition is enabled.
  useEffect(() => {
    if (!isOpen || !faceEnabled || !eventId) {
      setFaceStats(null);
      return;
    }
    let cancelled = false;
    setFaceStatsLoading(true);
    fetch(`/api/v1/events/${eventId}/face-stats`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => { if (!cancelled) setFaceStats(data); })
      .catch(() => { /* silent — section just won't render details */ })
      .finally(() => { if (!cancelled) setFaceStatsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, faceEnabled, eventId]);

  // Close venue dropdown on outside click
  useEffect(() => {
    if (!venueMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (venueMenuRef.current && !venueMenuRef.current.contains(e.target as Node)) {
        setVenueMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [venueMenuOpen]);

  if (!album) return null;

  // Hide LIVE badge once the parent event has ended
  const eventEnded = typeof album.eventId !== 'string' && album.eventId?.status === 'ended';
  const showLiveBadge = isLive && !eventEnded;

  // ── Engagement metrics ─────────────────────────────────────────────────
  const photos = activityStats?.totalPhotos ?? totalPhotoCount;
  const members = activityStats?.totalMembers ?? album.memberCount ?? 0;
  const likes = activityStats?.totalLikes ?? 0;
  const pendingProcessing = activityStats?.pendingProcessing ?? 0;

  // ── Face recognition derived ───────────────────────────────────────────
  const fs = faceStats;
  const indexedPercent = fs && fs.totalPhotos > 0 ? Math.round((fs.photosIndexed / fs.totalPhotos) * 100) : 0;
  const withFacesPercent = fs && fs.photosIndexed > 0 ? Math.round((fs.photosWithFaces / fs.photosIndexed) * 100) : 0;
  const memberFacePercent = fs && fs.totalMembers > 0 ? Math.round((fs.membersWithFaceRecognition / fs.totalMembers) * 100) : 0;
  const isIndexing = !!fs && fs.photosNotYetIndexed > 0;

  // ── ZIP details ────────────────────────────────────────────────────────
  const showZip = album.zipStatus === 'ready' && (album.zipPhotoCount ?? 0) > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Creator Controls" size="md">
      <div className={styles['body']}>
        {/* ── Status banner ─────────────────────────────────────────────── */}
        <div className={`${styles['status-banner']} ${showLiveBadge ? styles['status-banner-live'] : ''}`}>
          <div className={styles['status-left']}>
            {showLiveBadge ? (
              <LiveBadge active />
            ) : (
              <span className={styles['status-ended-pill']}>{eventEnded ? 'Ended' : 'Draft'}</span>
            )}
            <span className={styles['status-since']}>
              {showLiveBadge ? 'started' : eventEnded ? 'ended' : 'created'} {liveSinceLabel(album.createdAt)} ago
            </span>
          </div>
          <div className={styles['status-right']}>
            {pendingProcessing > 0 && (
              <span className={styles['processing-pill']}>
                <Zap className={styles['icon-xs']} />
                {pendingProcessing} processing
              </span>
            )}
          </div>
        </div>

        {/* ── Engagement section ────────────────────────────────────────── */}
        <section className={styles['section']}>
          <h4 className={styles['section-title']}>Engagement</h4>
          <div className={styles['metric-grid']}>
            <MetricTile icon={<Camera />} value={photos} label="Photos" />
            <MetricTile icon={<Users />} value={members} label="Members" />
            <MetricTile icon={<Heart />} value={likes} label="Likes" />
            {showZip && (
              <MetricTile
                icon={<FileArchive />}
                value={album.zipPhotoCount ?? 0}
                label="In ZIP"
                hint="Pre-built bundle ready for fast download"
              />
            )}
          </div>
        </section>

        {/* ── Face recognition section ──────────────────────────────────── */}
        {faceEnabled && (
          <section className={styles['section']}>
            <h4 className={styles['section-title']}>
              <ScanFace className={styles['section-icon']} aria-hidden />
              Face recognition
              <InfoTip label="What is face recognition?">
                Lets guests find themselves in photos via a selfie. Photos are scanned, members register a face, and matching runs at search time.
              </InfoTip>
            </h4>

            {faceStatsLoading && !fs && (
              <div className={styles['face-skeleton']}>Loading…</div>
            )}

            {fs && (
              <>
                {/* Indexing progress — only when actively indexing */}
                {isIndexing && (
                  <div className={styles['indexing-card']}>
                    <div className={styles['indexing-header']}>
                      <span className={styles['indexing-label']}>Indexing photos</span>
                      <span className={styles['indexing-count']}>
                        {fs.photosIndexed.toLocaleString()} / {fs.totalPhotos.toLocaleString()}
                      </span>
                    </div>
                    <div className={styles['progress-track']} aria-hidden>
                      <motion.div
                        className={styles['progress-fill']}
                        initial={{ width: 0 }}
                        animate={{ width: `${indexedPercent}%` }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                    <p className={styles['indexing-foot']}>
                      {fs.photosNotYetIndexed.toLocaleString()} remaining
                    </p>
                  </div>
                )}

                {/* Face stats grid */}
                <div className={styles['face-grid']}>
                  <ProgressCard
                    label="With faces"
                    value={fs.photosWithFaces}
                    total={fs.photosIndexed}
                    percent={withFacesPercent}
                  />
                  <ProgressCard
                    label="Indexed"
                    value={fs.photosIndexed}
                    total={fs.totalPhotos}
                    percent={indexedPercent}
                  />
                  <CountCard
                    label="Faces found"
                    value={fs.facesIndexedCount}
                    sub={`across ${fs.photosWithFaces.toLocaleString()} photo${fs.photosWithFaces === 1 ? '' : 's'}`}
                  />
                </div>

                {/* Member participation */}
                <div className={styles['face-grid']}>
                  <ProgressCard
                    label="Members with selfie"
                    value={fs.membersWithFaceRecognition}
                    total={fs.totalMembers}
                    percent={memberFacePercent}
                  />
                  <ProgressCard
                    label="Without selfie"
                    value={fs.membersWithoutFaceRecognition}
                    total={fs.totalMembers}
                    percent={fs.totalMembers > 0 ? 100 - memberFacePercent : 0}
                    tone="muted"
                  />
                </div>
              </>
            )}
          </section>
        )}

        {/* ── Actions section ───────────────────────────────────────────── */}
        {isLive && (
          <section className={styles['section']}>
            <h4 className={styles['section-title']}>
              <Sparkles className={styles['section-icon']} aria-hidden />
              Actions
            </h4>
            <div className={styles['actions-row']}>
              {photoCount > 0 && (
                <motion.button
                  type="button"
                  onClick={() => { onClose(); onPreviewSlideshow(); }}
                  className={styles['action-btn-primary']}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  title="Preview slideshow in this tab"
                >
                  <Presentation className={styles['icon-action']} />
                  Preview slideshow
                </motion.button>
              )}

              <div className={styles['venue-wrap']} ref={venueMenuRef}>
                <motion.button
                  type="button"
                  onClick={() => {
                    if (album.slideshowEnabled) {
                      setVenueMenuOpen((o) => !o);
                    } else {
                      onClose();
                      onSlideshowSetup();
                    }
                  }}
                  className={styles['action-btn-secondary']}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  title={album.slideshowEnabled ? 'Manage venue display' : 'Set up venue display'}
                >
                  <MonitorPlay className={styles['icon-action']} />
                  {album.slideshowEnabled ? 'Venue display' : 'Set up venue display'}
                  {album.slideshowEnabled && (
                    <>
                      <span className={styles['venue-status-dot']} />
                      <ChevronDown className={styles['icon-xs-down']} />
                    </>
                  )}
                </motion.button>

                <AnimatePresence>
                  {venueMenuOpen && album.slideshowEnabled && album.slideshowToken && (
                    <motion.div
                      className={styles['venue-dropdown']}
                      initial={{ opacity: 0, y: -4, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    >
                      {(() => {
                        const slideshowUrl = `${window.location.origin}/slideshow/${album._id}/${album.slideshowToken}`;
                        return (
                          <>
                            <button
                              type="button"
                              onClick={async () => {
                                setVenueMenuOpen(false);
                                try {
                                  await navigator.clipboard.writeText(slideshowUrl);
                                  toast.success('Slideshow URL copied!');
                                } catch {
                                  toast.error('Failed to copy');
                                }
                              }}
                              className={styles['menu-item']}
                            >
                              <Copy className={styles['icon-sm']} /> Copy URL
                            </button>
                            <a
                              href={slideshowUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setVenueMenuOpen(false)}
                              className={styles['menu-item']}
                            >
                              <ExternalLink className={styles['icon-sm']} /> Open in new tab
                            </a>
                            <button
                              type="button"
                              onClick={() => { setVenueMenuOpen(false); onClose(); onSlideshowSetup(); }}
                              className={styles['menu-item']}
                            >
                              <Settings className={styles['icon-sm']} /> Settings & password
                            </button>
                          </>
                        );
                      })()}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </section>
        )}

        {!isLive && photoCount === 0 && (
          <div className={styles['empty-hint']}>
            <ImageIcon className={styles['icon-sm']} aria-hidden />
            No photos yet — share the invite link to get started.
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

interface MetricTileProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  hint?: string;
}

function MetricTile({ icon, value, label, hint }: MetricTileProps) {
  return (
    <div className={styles['metric-tile']}>
      <div className={styles['metric-icon']}>{icon}</div>
      <div className={styles['metric-value']}>{value.toLocaleString()}</div>
      <div className={styles['metric-label']}>
        {label}
        {hint && <InfoTip label={`${label} info`}>{hint}</InfoTip>}
      </div>
    </div>
  );
}

interface ProgressCardProps {
  label: string;
  value: number;
  total: number;
  percent: number;
  tone?: 'default' | 'muted';
}

function ProgressCard({ label, value, total, percent, tone = 'default' }: ProgressCardProps) {
  return (
    <div className={`${styles['progress-card']} ${tone === 'muted' ? styles['progress-card-muted'] : ''}`}>
      <div className={styles['progress-card-label']}>{label}</div>
      <div className={styles['progress-card-row']}>
        <span className={styles['progress-card-value']}>{value.toLocaleString()}</span>
        <span className={styles['progress-card-total']}>/ {total.toLocaleString()}</span>
        <span className={styles['progress-card-spacer']} />
        <span className={styles['progress-card-percent']}>{percent}%</span>
      </div>
      <div className={styles['progress-track-thin']} aria-hidden>
        <motion.div
          className={`${styles['progress-fill-thin']} ${tone === 'muted' ? styles['progress-fill-thin-muted'] : ''}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, percent)}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}

interface CountCardProps {
  label: string;
  value: number;
  sub?: string;
}

function CountCard({ label, value, sub }: CountCardProps) {
  return (
    <div className={styles['count-card']}>
      <div className={styles['count-card-label']}>{label}</div>
      <div className={styles['count-card-value']}>{value.toLocaleString()}</div>
      {sub && <div className={styles['count-card-sub']}>{sub}</div>}
    </div>
  );
}
